import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { VanamLogo } from '@/components/VanamLogo';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { consentLinkWords } from '@/lib/consentLinkWords';

interface OpenResult {
  ok: boolean;
  reason?: 'not_found' | 'expired';
  stage?: 'draft' | 'signed';
  consent?: {
    id: string; consent_type: string; language_code: string; status: string;
    valid_from: string | null; valid_to: string | null;
    acknowledged_at: string | null; acknowledgement_method: string | null;
  };
}

/**
 * The page at the end of the link in a farmer's text message (owner, 2026-09-25: "message also
 * can be considered with link -- for text message only").
 *
 * The farmer has a phone and a link and no account, so this route sits OUTSIDE `RequireAuth` and
 * is the only page in the application that anybody may open. It asks `pub.fn_open_consent_link`,
 * which is bounded by hand: the wording, their name, their village, the dates. Nothing here can
 * ask the database anything else, because nothing else is granted to `anon`.
 *
 * The consent is its PDF - one document in English and the farmer's language (owner, 2026-09-28) -
 * streamed by the public consent-review function once it has checked the token: the signed copy
 * for a 'signed' link, the draft for a 'draft' one.
 */
export default function ConsentLinkPage() {
  const { token = '' } = useParams<{ token: string }>();

  const query = useQuery({
    queryKey: ['consent-link', token],
    enabled: isSupabaseConfigured && token.length > 20,
    retry: false,
    queryFn: async () => {
      const pub = getSupabase().schema('pub') as unknown as {
        rpc: (fn: string, args: Record<string, string>) => PromiseLike<{ data: OpenResult | null; error: { message: string } | null }>;
      };
      const { data, error } = await pub.rpc('fn_open_consent_link', { p_token: token });
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const pdfUrl = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/consent-review?token=${encodeURIComponent(token)}`;
  const pdf = useQuery({
    queryKey: ['consent-link-pdf', token],
    enabled: isSupabaseConfigured && Boolean(query.data?.ok),
    retry: false,
    queryFn: async () => {
      const response = await fetch(`${pdfUrl}&probe=1`);
      return response.ok;
    },
  });

  // In the consent's own language once it is known (owner, 2026-09-28: the seven message languages).
  const words = consentLinkWords(query.data?.consent?.language_code);
  const message = !token || token.length <= 20 ? words.incomplete
    : query.isLoading ? null
      : query.error ? words.failed
        : query.data?.reason === 'expired'
          // Told apart from every other refusal on purpose: an expired link is a real link, and
          // the farmer should ask for a new one rather than think they were sent a fake.
          ? words.expired
          : query.data?.ok ? (pdf.data === false ? words.invalid : null)
            : words.invalid;

  return (
    <div className="consent-link-page">
      <header>
        <VanamLogo height={40} title="Vanam" variant="compact" />
        <p className="muted">{words.title}</p>
      </header>

      {query.isLoading && <p className="state-note">{words.opening}</p>}
      {message && <p className="state-note is-warn">{message}</p>}

      {pdf.data && (
        <>
          <iframe className="consent-link-frame" src={pdfUrl} title={words.title} />
          <p className="muted">
            {words.pdfBefore} <a href={pdfUrl} rel="noreferrer" target="_blank">{words.pdfLink}</a> {words.pdfAfter}
          </p>
        </>
      )}

    </div>
  );
}
